import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  NumberInput,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  Stepper,
  Table,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconCheck,
  IconFileTypePdf,
  IconMessage,
  IconNote,
  IconPlus,
  IconSearch,
  IconTruck,
  IconTruckDelivery,
  IconX,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createSupplyRequest,
  getSupplyCategories,
  getSupplyItems,
  getSupplyRequest,
  getSupplyRequests,
  updateSupplyRequest,
} from "@/api/supply";
import { getBranches } from "@/api/branches";
import { getBrand } from "@/api/tenant";
import {
  ListCell,
  ListEmpty,
  ListError,
  ListHeader,
  ListPagination,
  ListRow,
  ListScroll,
  ListSkeleton,
} from "@/components/DataList";
import { ModalTitle } from "@/components/ModalTitle";
import { PageTitle } from "@/components/PageTitle";
import { useAccess } from "@/hooks/useAccess";
import { lockProps } from "@/lib/permissions";
import { notify } from "@/lib/notify";
import { downloadSupplyPdf } from "@/lib/supplyPdf";
import { RequestComposer } from "./RequestComposer";

const REQUEST_COLUMNS = [
  { label: "Talep", w: 80 },
  { label: "Şube", flex: true, ta: "left" },
  { label: "Durum", w: 150 },
  { label: "Tarih", w: 170 },
  { label: "İşlem", w: 136 },
];

const PRINTABLE = new Set(["approved", "shipped", "received"]);

const PDF_HINT = "Talep onaylanınca indirilebilir";

const STATUS = {
  submitted: { label: "Onay bekliyor", color: "yellow" },
  approved: { label: "Onaylandı", color: "blue" },
  rejected: { label: "Reddedildi", color: "red" },
  shipped: { label: "Yolda", color: "indigo" },
  received: { label: "Teslim alındı", color: "teal" },
  cancelled: { label: "İptal", color: "gray" },
};

const QTY_FIELD = {
  approved: "approvedQty",
  shipped: "shippedQty",
  received: "receivedQty",
};

const DATE_FORMAT = new Intl.DateTimeFormat("tr-TR", {
  dateStyle: "short",
  timeStyle: "short",
});

const STATUS_OPTIONS = [
  { value: "all", label: "Tüm durumlar" },
  ...Object.entries(STATUS).map(([value, meta]) => ({
    value,
    label: meta.label,
  })),
];

export default function SupplyRequests() {
  const queryClient = useQueryClient();
  const { can } = useAccess();
  const [createModal, create] = useDisclosure(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectNote, setRejectNote] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [openId, setOpenId] = useState(
    () => Number(searchParams.get("id")) || null,
  );
  const [lineQty, setLineQty] = useState({});
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [downloadingId, setDownloadingId] = useState(null);

  const canDecide = can("supply_center");
  const canRequest = can("supply");

  const requests = useQuery({
    queryKey: ["supply-requests"],
    queryFn: getSupplyRequests,
  });
  const branches = useQuery({ queryKey: ["branches"], queryFn: getBranches });
  const brand = useQuery({ queryKey: ["brand"], queryFn: getBrand });
  // Kalem listesi yalnızca talep oluştururken ve detayda gerekiyor; liste ekranında
  // beklerken çekmek boşuna.
  const items = useQuery({
    queryKey: ["supply-items"],
    queryFn: getSupplyItems,
    enabled: createModal || openId !== null,
  });
  const categories = useQuery({
    queryKey: ["supply-categories"],
    queryFn: getSupplyCategories,
    enabled: createModal,
  });

  useEffect(() => {
    if (searchParams.has("id")) {
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const detail = useQuery({
    queryKey: ["supply-request", openId],
    queryFn: () => getSupplyRequest(openId),
    enabled: Boolean(openId),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["supply-requests"] });
    queryClient.invalidateQueries({ queryKey: ["supply-request", openId] });
    queryClient.invalidateQueries({ queryKey: ["supply-items"] });
  };

  const send = useMutation({
    mutationFn: createSupplyRequest,
    onSuccess: () => {
      refresh();
      create.close();
      notify.success("Talep gönderildi");
    },
    onError: (error) => notify.error(error),
  });

  const decide = useMutation({
    mutationFn: ({ status, lines, decisionNote }) =>
      updateSupplyRequest(openId, { status, lines, decisionNote }),
    onSuccess: () => {
      refresh();
      setRejecting(false);
      setRejectNote("");
      notify.success("Talep güncellendi");
    },
    onError: (error) => notify.error(error),
  });

  // Her aşamanın kendi tavanı var: onay istenene, sevk onaya, teslim sevke kadar. Teslim
  // varsayılanı sevk miktarı — onaydan devralmak, eksik gelen sevkiyatta olmayan malı
  // şube deposuna yazıyordu.
  const ceilingOf = (line, status) =>
    status === "submitted"
      ? line.requestedQty
      : status === "approved"
        ? line.approvedQty
        : line.shippedQty;

  const qtyFor = (line) =>
    lineQty[line.id] ??
    ceilingOf(line, detail.data?.status) ??
    line.requestedQty;

  const submitDecision = (status) => {
    if (status === "rejected" || status === "cancelled") {
      decide.mutate({ status });
      return;
    }

    const field = QTY_FIELD[status];

    decide.mutate({
      status,
      lines: (detail.data?.lines ?? []).map((line) => ({
        id: line.id,
        [field]: qtyFor(line),
      })),
    });
  };

  const downloadPdf = async (id) => {
    setDownloadingId(id);

    try {
      const [request, itemList, categoryList] = await Promise.all([
        queryClient.fetchQuery({
          queryKey: ["supply-request", id],
          queryFn: () => getSupplyRequest(id),
        }),
        queryClient.ensureQueryData({
          queryKey: ["supply-items"],
          queryFn: getSupplyItems,
        }),
        queryClient.ensureQueryData({
          queryKey: ["supply-categories"],
          queryFn: getSupplyCategories,
        }),
      ]);

      await downloadSupplyPdf({
        brandName: brand.data?.name ?? "Noxus",
        request,
        statusLabel: STATUS[request.status]?.label ?? request.status,
        items: itemList,
        categories: categoryList,
      });
    } catch {
      notify.error({
        title: "PDF oluşturulamadı",
        message: "Bir sorun çıktı, tekrar deneyin.",
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const branchById = useMemo(
    () =>
      new Map((branches.data ?? []).map((branch) => [branch.id, branch.name])),
    [branches.data],
  );

  const itemById = useMemo(
    () => new Map((items.data ?? []).map((item) => [item.id, item])),
    [items.data],
  );

  const branchOptions = useMemo(
    () =>
      (branches.data ?? []).map((branch) => ({
        value: String(branch.id),
        label: branch.name,
      })),
    [branches.data],
  );

  const branchName = (id) => branchById.get(id) ?? "—";

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();

    return (requests.data ?? []).filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) {
        return false;
      }
      return term
        ? (branchById.get(row.branchId) ?? "").toLowerCase().includes(term)
        : true;
    });
  }, [requests.data, branchById, search, statusFilter]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  // Girilen miktarlar talebe bağlı: modal kapanıp başka talep açıldığında eski değerler
  // taşınıyor ve ekranda görünenden farklı bir sayı gönderiliyordu.
  useEffect(() => {
    setLineQty({});
    setRejecting(false);
    setRejectNote("");
  }, [openId, detail.data?.status]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const pageItems = filtered.slice((page - 1) * perPage, page * perPage);

  const request = detail.data;
  const itemName = (id) => itemById.get(id)?.name ?? `#${id}`;
  const itemUnit = (id) => itemById.get(id)?.unit ?? "";
  // Sevk edilmiş talepte de giriş açık: eksik gelen sevkiyatı şube ancak burada bildirebiliyor.
  const editingQty =
    (canDecide &&
      (request?.status === "submitted" || request?.status === "approved")) ||
    (request?.status === "shipped" && (canDecide || canRequest));

  return (
    <Stack gap="lg">
      <PageTitle>İkmal Talepleri</PageTitle>
      <Group justify="space-between">
        <Group>
          <ThemeIcon size={46} radius="md" variant="light" color="chocolate">
            <IconTruckDelivery size={26} stroke={1.6} />
          </ThemeIcon>
          <div>
            <Title order={2}>İkmal Talepleri</Title>
            <Text c="dimmed" size="sm">
              Şube taleplerini takip et, onayla ve merkez deposundan sevk et
            </Text>
          </div>
        </Group>
        {/* Yetkisiz kullanıcıda gizlenmiyor, kilitleniyor. */}
        <Button
          leftSection={<IconPlus size={16} />}
          {...lockProps(canRequest)}
          onClick={create.open}
        >
          Talep oluştur
        </Button>
      </Group>

      <Card withBorder radius="md" p="md">
        <Group justify="center" mb="md">
          <TextInput
            w={260}
            placeholder="Şube adı ara"
            leftSection={<IconSearch size={15} />}
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
          />
          <Select
            aria-label="Duruma göre filtrele"
            w={200}
            withCheckIcon={false}
            allowDeselect={false}
            data={STATUS_OPTIONS}
            value={statusFilter}
            onChange={setStatusFilter}
          />
        </Group>

        <Card withBorder radius="md" p={0}>
          {requests.isLoading ? (
            <ListScroll minWidth={700}>
              <ListHeader columns={REQUEST_COLUMNS} />
              <ListSkeleton
                columns={REQUEST_COLUMNS}
                rows={perPage > 10 ? 10 : perPage}
              />
            </ListScroll>
          ) : requests.isError ? (
            <ListError title="Talepler alınamadı" onRetry={requests.refetch} />
          ) : (
            <>
              <ListScroll minWidth={700}>
                <ListHeader columns={REQUEST_COLUMNS} />

                {pageItems.length === 0 ? (
                  <ListEmpty>
                    {search || statusFilter !== "all"
                      ? "Talep bulunamadı"
                      : "Henüz talep yok"}
                  </ListEmpty>
                ) : (
                  pageItems.map((row) => (
                    <ListRow key={row.id}>
                      <ListCell w={80}>#{row.id}</ListCell>
                      <ListCell flex ta="left">
                        <Text truncate>{branchName(row.branchId)}</Text>
                      </ListCell>
                      <ListCell w={150}>
                        <Badge
                          variant="light"
                          tt="none"
                          color={STATUS[row.status]?.color}
                        >
                          {STATUS[row.status]?.label ?? row.status}
                        </Badge>
                      </ListCell>
                      <ListCell w={170}>
                        {DATE_FORMAT.format(new Date(row.createdAt))}
                      </ListCell>
                      <ListCell w={136}>
                        <Group gap={4} justify="center" wrap="nowrap">
                          <Button
                            size="compact-sm"
                            variant="subtle"
                            onClick={() => setOpenId(row.id)}
                          >
                            Aç
                          </Button>
                          <ActionIcon
                            variant="subtle"
                            color="chocolate"
                            aria-label={`Talep #${row.id} PDF indir`}
                            title={
                              PRINTABLE.has(row.status) ? "PDF indir" : PDF_HINT
                            }
                            disabled={!PRINTABLE.has(row.status)}
                            loading={downloadingId === row.id}
                            onClick={() => downloadPdf(row.id)}
                          >
                            <IconFileTypePdf size={18} />
                          </ActionIcon>
                        </Group>
                      </ListCell>
                    </ListRow>
                  ))
                )}
              </ListScroll>

              <ListPagination
                page={page}
                onPage={setPage}
                perPage={perPage}
                onPerPage={(value) => {
                  setPerPage(value);
                  setPage(1);
                }}
                total={filtered.length}
                unit="talep"
                totalPages={totalPages}
              />
            </>
          )}
        </Card>
      </Card>

      <RequestComposer
        opened={createModal}
        onClose={create.close}
        branches={branchOptions}
        items={items.data ?? []}
        categories={categories.data ?? []}
        sending={send.isPending}
        onSubmit={(values) => send.mutate(values)}
      />

      <Modal
        opened={Boolean(openId)}
        onClose={() => setOpenId(null)}
        title={<ModalTitle icon={IconTruck} title={`Talep #${openId ?? ""}`} />}
        size="lg"
        styles={{
          content: {
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
          body: { display: "flex", flexDirection: "column", minHeight: 0 },
        }}
      >
        {detail.isLoading || !request ? (
          <Text size="sm" c="dimmed">
            Yükleniyor…
          </Text>
        ) : (
          <Stack gap="lg" mih={0}>
            {/* Durum ve şube tek satırda: ikisi birlikte "kimin talebi, nerede" sorusunu
                            cevaplıyor ve ayrı bloklara bölündüğünde göz iki kez duruyordu. */}
            <Group justify="space-between" wrap="nowrap">
              <Group gap="xs">
                <Badge
                  size="lg"
                  variant="light"
                  tt="none"
                  color={STATUS[request.status]?.color}
                >
                  {STATUS[request.status]?.label ?? request.status}
                </Badge>
                <Text size="sm" c="dimmed">
                  {branchName(request.branchId)}
                </Text>
              </Group>
              <Text size="xs" c="dimmed">
                {DATE_FORMAT.format(new Date(request.createdAt))}
              </Text>
            </Group>

            {/* Not bir alıntı: gövde metniyle aynı biçimde yazıldığında kalemlerle
                            karışıyor ve kimin yazdığı belirsiz kalıyordu. */}
            {request.note ? (
              <Alert
                variant="light"
                color="gray"
                icon={<IconNote size={16} />}
                style={{ flexShrink: 0 }}
                title="Şubenin notu"
              >
                {request.note}
              </Alert>
            ) : null}

            {request.decisionNote ? (
              <Alert
                variant="light"
                color={request.status === "rejected" ? "red" : "blue"}
                style={{ flexShrink: 0 }}
                icon={<IconMessage size={16} />}
                title="Merkezin yanıtı"
              >
                {request.decisionNote}
              </Alert>
            ) : null}

            {/* Kalemler tabloda: dört miktar (istenen/onaylanan/sevk/teslim) sütun
                            hizasında okununca fark tek bakışta görülüyor, satır içi metinde değil. */}
            <ScrollArea
              type="auto"
              offsetScrollbars="y"
              style={{ flex: "0 1 auto", minHeight: 0 }}
              styles={{
                root: { display: "flex", flexDirection: "column" },
                viewport: { flex: "0 1 auto", minHeight: 0, height: "auto" },
              }}
            >
              <Table stickyHeader verticalSpacing="sm" horizontalSpacing="xs">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Kalem</Table.Th>
                    <Table.Th ta="right" w={90}>
                      İstenen
                    </Table.Th>
                    <Table.Th ta="right" w={90}>
                      Onaylanan
                    </Table.Th>
                    <Table.Th ta="right" w={90}>
                      Sevk
                    </Table.Th>
                    {editingQty ? (
                      <Table.Th ta="right" w={110}>
                        {request.status === "submitted" ? "Onayla" : "Sevk et"}
                      </Table.Th>
                    ) : null}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {request.lines.map((line) => (
                    <Table.Tr key={line.id}>
                      <Table.Td>
                        <Text size="sm" fw={500}>
                          {itemName(line.itemId)}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {itemUnit(line.itemId)}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="right">{line.requestedQty}</Table.Td>
                      <Table.Td
                        ta="right"
                        c={line.approvedQty == null ? "dimmed" : undefined}
                      >
                        {line.approvedQty ?? "—"}
                      </Table.Td>
                      <Table.Td
                        ta="right"
                        c={line.shippedQty == null ? "dimmed" : undefined}
                      >
                        {line.shippedQty ?? "—"}
                      </Table.Td>
                      {editingQty ? (
                        <Table.Td>
                          <NumberInput
                            size="xs"
                            min={0}
                            max={ceilingOf(line, request.status) ?? undefined}
                            value={qtyFor(line)}
                            onChange={(value) =>
                              setLineQty((prev) => ({
                                ...prev,
                                [line.id]: value === "" ? 0 : Number(value),
                              }))
                            }
                          />
                        </Table.Td>
                      ) : null}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>

            {/* Yatay adım göstergesi: dikey zaman çizelgesi dört satırla modalın yarısını
                            kaplıyordu, oysa taşıdığı bilgi tek satırlık. */}
            <Stepper active={STEP[request.status] ?? 0} size="xs" iconSize={22}>
              <Stepper.Step label="Onay bekliyor" />
              <Stepper.Step label="Onaylandı" />
              <Stepper.Step label="Sevk edildi" />
              <Stepper.Step label="Teslim alındı" />
            </Stepper>

            <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
              {[
                {
                  label: "Talep eden",
                  name: request.createdByName,
                  at: request.createdAt,
                },
                {
                  label:
                    request.status === "rejected" ? "Reddeden" : "Onaylayan",
                  name: request.decidedByName,
                  at: request.decidedAt,
                },
                {
                  label: "Sevk eden",
                  name: request.shippedByName,
                  at: request.shippedAt,
                },
                {
                  label: "Teslim alan",
                  name: request.receivedByName,
                  at: request.receivedAt,
                },
              ].map((actor) => (
                <div key={actor.label}>
                  <Text size="xs" c="dimmed">
                    {actor.label}
                  </Text>
                  <Text
                    size="sm"
                    fw={500}
                    c={actor.name ? undefined : "dimmed"}
                    truncate
                  >
                    {actor.name ?? "—"}
                  </Text>
                  {actor.at ? (
                    <Text size="xs" c="dimmed">
                      {DATE_FORMAT.format(new Date(actor.at))}
                    </Text>
                  ) : null}
                </div>
              ))}
            </SimpleGrid>

            {rejecting ? (
              <Alert
                color="red"
                variant="light"
                title="Talebi reddet"
                style={{ flexShrink: 0 }}
              >
                <Stack gap="sm">
                  <Textarea
                    label="Gerekçe"
                    description="Şubeye bildirimle gider ve bu ekranda kalır."
                    placeholder="Merkezde stok yok, hafta sonu tedarik edilecek"
                    autosize
                    minRows={2}
                    value={rejectNote}
                    onChange={(event) =>
                      setRejectNote(event.currentTarget.value)
                    }
                  />
                  <Group justify="flex-end">
                    <Button
                      variant="default"
                      onClick={() => setRejecting(false)}
                    >
                      Vazgeç
                    </Button>
                    <Button
                      color="red"
                      leftSection={<IconX size={16} />}
                      loading={decide.isPending}
                      disabled={!rejectNote.trim()}
                      onClick={() =>
                        decide.mutate({
                          status: "rejected",
                          decisionNote: rejectNote.trim(),
                        })
                      }
                    >
                      Reddet
                    </Button>
                  </Group>
                </Stack>
              </Alert>
            ) : (
              <Group justify="space-between">
                <Button
                  variant="default"
                  leftSection={<IconFileTypePdf size={16} />}
                  title={PRINTABLE.has(request.status) ? undefined : PDF_HINT}
                  disabled={!PRINTABLE.has(request.status)}
                  loading={downloadingId === request.id}
                  onClick={() => downloadPdf(request.id)}
                >
                  PDF indir
                </Button>

                <Group gap="sm">
                  {canDecide && request.status === "submitted" ? (
                    <>
                      <Button
                        variant="light"
                        color="red"
                        leftSection={<IconX size={16} />}
                        onClick={() => setRejecting(true)}
                      >
                        Reddet
                      </Button>
                      <Button
                        leftSection={<IconCheck size={16} />}
                        loading={decide.isPending}
                        onClick={() => submitDecision("approved")}
                      >
                        Onayla
                      </Button>
                    </>
                  ) : null}

                  {canDecide && request.status === "approved" ? (
                    <Tooltip label="Sevk edilen miktar merkez stoğundan düşer">
                      <Button
                        leftSection={<IconTruck size={16} />}
                        loading={decide.isPending}
                        onClick={() => submitDecision("shipped")}
                      >
                        Sevk et
                      </Button>
                    </Tooltip>
                  ) : null}

                  {request.status === "shipped" ? (
                    <Button
                      leftSection={<IconCheck size={16} />}
                      loading={decide.isPending}
                      onClick={() => submitDecision("received")}
                    >
                      Teslim aldım
                    </Button>
                  ) : null}
                </Group>
              </Group>
            )}
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}

// Mantine'de `active` AKTİF adımın indeksi: öncekiler tamamlanmış çizilir, kendisi vurgulanır.
// Değerler bir fazla verildiğinde onay bekleyen talepte "Onaylandı" da dolu görünüyordu.
const STEP = {
  submitted: 0,
  approved: 1,
  shipped: 2,
  received: 3,
  rejected: 0,
  cancelled: 0,
};
